import { ChangeDetectorRef, Component, OnDestroy, OnInit, Pipe, PipeTransform } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import { AdminService } from '../../../services/admin.service';
import { CalendarDayResponse } from '../../../models/calendar-day.model';
import {
  CalendarMarker,
  CalendarMarkerArchiveRequest,
  CalendarMarkerCreateRequest,
  CalendarMarkerGroup,
  CalendarMarkerUpdateRequest
} from '../../../models/calendar-marker.model';
import {
  CalendarTimezoneMarkerArchiveResult,
  CalendarTimezoneMarkerCreateResult,
  CalendarTimezoneMarkerEditResult,
  CalendarTimezoneMarkerModalComponent,
  CalendarTimezoneMarkerModalMode,
  CalendarTimezoneMarkerModalModes,
  CalendarTimezoneMarkerModalResult
} from './calendar-timezone-marker-modal.component';

const DAYS_IN_CALENDAR = 42;
const WEEKEND_MODE = 'weekend';
const HOLIDAY_MODE = 'holiday';
const CREATE_MARKER_PENDING_ID = '__calendar_marker_create__';
const FALLBACK_DEFAULT_MARKER_NAME = 'Company default calendar';
const EMPTY_MARKER_VALUE = '';
const FALLBACK_DEFAULT_MARKER_ACTIVE = true;
const FALLBACK_DEFAULT_MARKER_STOCK = true;
const FALLBACK_DEFAULT_MARKER_DEPARTMENTS_COUNT = 0;
const FALLBACK_DEFAULT_MARKER_ZONE_ID = 'Europe/Minsk';
const FALLBACK_DEFAULT_MARKER_TIMEZONE_ASSIGNMENT_REQUIRED = false;
const MONTH_KEYS = [
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER'
];
const WEEK_DAY_KEYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY'
];

interface CalendarDayConfig {
  currentMonthIndex: number;
  isFirstVisibleDay: boolean;
  calendarDays: CalendarDayResponse[];
}

export class CalendarDay {
  public date: Date;
  public dateKey: string;
  public dateNumber: number;
  public id: string;
  public monthKey: string;
  public isToday: boolean;
  public isCurrentMonth: boolean;
  public isWeekend: boolean;
  public isPublicHoliday: boolean;
  public showMonthLabel: boolean;

  constructor(date: Date, config: CalendarDayConfig) {
    this.date = date;
    this.dateKey = getDateKey(date);
    this.dateNumber = date.getDate();
    this.monthKey = MONTH_KEYS[date.getMonth()];
    this.isToday = isSameDate(date, new Date());
    this.isCurrentMonth = date.getMonth() === config.currentMonthIndex;
    this.showMonthLabel = this.isToday || config.isFirstVisibleDay || this.dateNumber === 1;
    this.applyCalendarDay(config.calendarDays);
  }

  private applyCalendarDay(calendarDays: CalendarDayResponse[]): void {
    var calendarDay = findCalendarDay(calendarDays, this.dateKey);
    this.id = calendarDay ? calendarDay.id : '';
    this.isWeekend = !!calendarDay;
    this.isPublicHoliday = calendarDay ? calendarDay.isPublicHoliday : false;
  }
}

@Pipe({
  name: 'chunk',
})
export class ChunkPipe implements PipeTransform {
  transform(calendarDaysArray: CalendarDay[], chunkSize: number): CalendarDay[][] {
    var calendarRows: CalendarDay[][] = [];
    var weekDays: CalendarDay[] = [];
    calendarDaysArray.forEach((day: CalendarDay, index: number) => {
      weekDays.push(day);
      if ((index + 1) % chunkSize === 0) {
        calendarRows.push(weekDays);
        weekDays = [];
      }
    });
    return calendarRows;
  }
}

@Component({
  selector: 'app-task-fullview-popup',
  templateUrl: './task-fullview-popup.component.html',
  styleUrls: ['./task-fullview-popup.component.css']
})
export class TaskFullviewPopupComponent implements OnInit, OnDestroy {
  public calendar: CalendarDay[] = [];
  public departments: CalendarMarkerGroup[] = [];
  public markers: CalendarMarker[] = [];
  public weekDayKeys = WEEK_DAY_KEYS;
  public displayMonthKey: string;
  public displayYears: number;
  public activeMarkerId = '';
  public editMode = WEEKEND_MODE;
  public isLoading = false;
  public loadError = false;
  public saveError = false;
  public pendingCalendarDayKey = '';
  public pendingMarkerMutationId = '';

  private monthIndex = 0;
  private from = '';
  private to = '';
  private calendarDays: CalendarDayResponse[] = [];
  private destroy$ = new Subject<void>();
  private isDestroyed = false;
  private isViewRefreshQueued = false;

  constructor(
    private adminService: AdminService,
    private changeDetectorRef: ChangeDetectorRef,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  public increaseMonth(): void {
    this.monthIndex++;
    this.generateCalendarDays();
    this.loadCalendarDays();
  }

  public decreaseMonth(): void {
    this.monthIndex--;
    this.generateCalendarDays();
    this.loadCalendarDays();
  }

  public setCurrentMonth(): void {
    this.monthIndex = 0;
    this.generateCalendarDays();
    this.loadCalendarDays();
  }

  public selectMarker(marker: CalendarMarker): void {
    if (!marker) {
      return;
    }
    this.activeMarkerId = this.getMarkerId(marker);
    this.calendarDays = [];
    this.generateCalendarDays();
    this.loadCalendarDays();
  }

  public openMarkerCreation(): void {
    this.openMarkerDialog(CalendarTimezoneMarkerModalModes.CREATE, null);
  }

  public openMarkerEdit(marker: CalendarMarker, event: Event): void {
    event.stopPropagation();
    if (!marker || marker.stock) {
      return;
    }
    this.openMarkerDialog(CalendarTimezoneMarkerModalModes.EDIT, marker);
  }

  public openMarkerArchive(marker: CalendarMarker, event: Event): void {
    event.stopPropagation();
    if (!marker || marker.stock) {
      return;
    }
    this.openMarkerDialog(CalendarTimezoneMarkerModalModes.ARCHIVE, marker);
  }

  public isMarkerMutationPending(marker?: CalendarMarker): boolean {
    if (!this.pendingMarkerMutationId) {
      return false;
    }
    if (!marker) {
      return true;
    }
    return this.pendingMarkerMutationId === this.getMarkerId(marker);
  }

  public selectWeekendEditMode(): void {
    this.editMode = WEEKEND_MODE;
  }

  public selectHolidayEditMode(): void {
    this.editMode = HOLIDAY_MODE;
  }

  public updateCalendarDay(day: CalendarDay): void {
    if (!day || this.isCalendarDayPending(day)) {
      return;
    }
    if (this.isHolidayEditMode) {
      this.toggleHoliday(day);
      return;
    }
    this.toggleWeekend(day);
  }

  public isCalendarDayPending(day: CalendarDay): boolean {
    return !!day && this.pendingCalendarDayKey === day.dateKey;
  }

  public toggleDepartment(marker: CalendarMarker, department: CalendarMarkerGroup, event: Event): void {
    event.stopPropagation();
    if (this.isDepartmentAssignedToMarker(marker, department)) {
      this.removeDepartmentFromMarker(marker, department);
      return;
    }
    this.assignDepartmentToMarker(marker, department);
  }

  public isDepartmentAssignedToMarker(marker: CalendarMarker, department: CalendarMarkerGroup): boolean {
    if (!marker || !department) {
      return false;
    }
    if (this.isDefaultMarker(marker)) {
      return this.isDepartmentAssignedToDefault(department);
    }
    return this.getDepartmentMarkerId(department) === this.getMarkerId(marker);
  }

  public isMarkerActive(marker: CalendarMarker): boolean {
    return marker && this.getMarkerId(marker) === this.activeMarkerId;
  }

  public getDepartmentCount(marker: CalendarMarker): number {
    if (!marker) {
      return 0;
    }
    if (this.departments.length) {
      return this.countDepartmentsByMarker(marker);
    }
    return typeof marker.assignedDepartmentsCount === 'number' ? marker.assignedDepartmentsCount : 0;
  }

  public getMarkerDisplayName(marker: CalendarMarker): string {
    if (!marker) {
      return '';
    }
    return marker.displayName || marker.zoneId || marker.name || this.getMarkerId(marker);
  }

  public getMarkerTimezoneSummary(marker: CalendarMarker): string {
    if (!marker || marker.timezoneAssignmentRequired) {
      return '';
    }
    var summary: string[] = [];
    if (marker.currentOffset) {
      summary.push(marker.currentOffset);
    }
    if (marker.currentLocalTime) {
      summary.push(marker.currentLocalTime);
    }
    return summary.join(' | ');
  }

  public trackByMarkerId(index: number, marker: CalendarMarker): string {
    return marker ? marker.id || '' : '';
  }

  public trackByDepartmentId(index: number, department: CalendarMarkerGroup): string {
    return department.id;
  }

  public trackByDayKey(index: number, dayKey: string): string {
    return dayKey;
  }

  public trackByCalendarDay(index: number, day: CalendarDay): string {
    return day.dateKey;
  }

  public trackByWeekIndex(index: number): number {
    return index;
  }

  public get isWeekendEditMode(): boolean {
    return this.editMode === WEEKEND_MODE;
  }

  public get isHolidayEditMode(): boolean {
    return this.editMode === HOLIDAY_MODE;
  }

  private loadInitialData(): void {
    this.isLoading = true;
    this.loadError = false;
    this.generateCalendarDays();
    this.loadMarkers();
  }

  private loadMarkers(): void {
    this.adminService.getCalendarMarkers().takeUntil(this.destroy$).subscribe(
      (response: HttpResponse<CalendarMarker[]>) => this.handleMarkersLoaded(response),
      () => this.handleMarkersLoadError()
    );
  }

  private handleMarkersLoaded(response: HttpResponse<CalendarMarker[]>): void {
    this.markers = this.resolveVisibleMarkers(this.toMarkerArray(response.body));
    this.activeMarkerId = this.resolveActiveMarkerId();
    this.pendingMarkerMutationId = '';
    this.loadDepartments();
  }

  private handleMarkersLoadError(): void {
    this.markers = [this.buildFallbackDefaultMarker()];
    this.activeMarkerId = '';
    this.departments = [];
    this.pendingMarkerMutationId = '';
    this.loadCalendarDays();
  }

  private loadDepartments(): void {
    this.adminService.getCalendarMarkerGroups().takeUntil(this.destroy$).subscribe(
      (response: HttpResponse<CalendarMarkerGroup[]>) => this.handleDepartmentsLoaded(response),
      () => this.handleDepartmentsLoadError()
    );
  }

  private handleDepartmentsLoaded(response: HttpResponse<CalendarMarkerGroup[]>): void {
    this.departments = this.sortDepartments(response.body || []);
    this.saveError = false;
    this.loadCalendarDays();
  }

  private handleDepartmentsLoadError(): void {
    this.departments = [];
    this.loadCalendarDays();
  }

  private loadCalendarDays(): void {
    this.isLoading = true;
    this.adminService.getListWeekends(this.getCalendarRequestFrom(), this.getCalendarRequestTo(), this.activeMarkerId).takeUntil(this.destroy$).subscribe(
      (response: HttpResponse<CalendarDayResponse[]>) => this.handleCalendarDaysLoaded(response),
      () => this.handleLoadError()
    );
  }

  private handleCalendarDaysLoaded(response: HttpResponse<CalendarDayResponse[]>): void {
    this.calendarDays = response.body || [];
    this.pendingCalendarDayKey = '';
    this.isLoading = false;
    this.loadError = false;
    this.saveError = false;
    this.generateCalendarDays();
    this.markForCheck();
  }

  private openMarkerDialog(mode: CalendarTimezoneMarkerModalMode, marker: CalendarMarker): void {
    if (this.isMarkerMutationPending()) {
      return;
    }

    var markerForDialog = this.copyMarkerForDialog(marker);
    var modalRef: NgbModalRef = this.modalService.open(CalendarTimezoneMarkerModalComponent, {
      size: 'lg'
    });
    modalRef.componentInstance.configure(mode, markerForDialog, this.markers);
    modalRef.result.then(
      (result: CalendarTimezoneMarkerModalResult) => this.handleMarkerDialogResult(result),
      () => {}
    );
  }

  private handleMarkerDialogResult(result: CalendarTimezoneMarkerModalResult): void {
    if (!result || !result.mode) {
      return;
    }
    if (this.isCreateMarkerResult(result)) {
      this.createMarker(result);
      return;
    }
    if (this.isEditMarkerResult(result)) {
      this.updateMarker(result);
      return;
    }
    this.archiveMarker(result);
  }

  private createMarker(result: CalendarTimezoneMarkerCreateResult): void {
    this.pendingMarkerMutationId = CREATE_MARKER_PENDING_ID;
    this.saveError = false;
    this.adminService.createCalendarMarker(this.buildMarkerCreateRequest(result)).takeUntil(this.destroy$).subscribe(
      (response: HttpResponse<CalendarMarker>) => this.handleMarkerCreated(response),
      () => this.handleMarkerSaveError()
    );
  }

  private handleMarkerCreated(response: HttpResponse<CalendarMarker>): void {
    this.activeMarkerId = this.resolveCreatedMarkerId(response);
    this.loadMarkers();
  }

  private updateMarker(result: CalendarTimezoneMarkerEditResult): void {
    this.pendingMarkerMutationId = result.markerId;
    this.saveError = false;
    this.adminService.updateCalendarMarker(result.markerId, this.buildMarkerUpdateRequest(result))
      .takeUntil(this.destroy$).subscribe(
        () => this.handleMarkerUpdated(result.markerId),
        () => this.handleMarkerSaveError()
      );
  }

  private handleMarkerUpdated(markerId: string): void {
    this.activeMarkerId = markerId;
    this.loadMarkers();
  }

  private archiveMarker(result: CalendarTimezoneMarkerArchiveResult): void {
    this.pendingMarkerMutationId = result.markerId;
    this.saveError = false;
    this.adminService.archiveCalendarMarker(result.markerId, this.buildMarkerArchiveRequest(result))
      .takeUntil(this.destroy$).subscribe(
        () => this.handleMarkerArchived(result.markerId),
        () => this.handleMarkerSaveError()
      );
  }

  private handleMarkerArchived(markerId: string): void {
    if (this.activeMarkerId === markerId) {
      this.activeMarkerId = '';
    }
    this.loadMarkers();
  }

  private assignDepartmentToMarker(marker: CalendarMarker, department: CalendarMarkerGroup): void {
    if (this.isDefaultMarker(marker)) {
      this.removeDepartmentFromPreviousMarker(department);
      return;
    }
    this.replaceMarkerDepartments(marker, department);
  }

  private removeDepartmentFromMarker(marker: CalendarMarker, department: CalendarMarkerGroup): void {
    if (this.isDefaultMarker(marker)) {
      return;
    }
    this.deleteDepartmentFromMarker(this.getMarkerId(marker), department.id);
  }

  private removeDepartmentFromPreviousMarker(department: CalendarMarkerGroup): void {
    if (this.isDepartmentAssignedToDefault(department)) {
      return;
    }
    this.deleteDepartmentFromMarker(this.getDepartmentMarkerId(department), department.id);
  }

  private replaceMarkerDepartments(marker: CalendarMarker, department: CalendarMarkerGroup): void {
    this.adminService.replaceCalendarMarkerGroups(this.getMarkerId(marker), this.buildTargetGroupIds(marker, department)).takeUntil(this.destroy$).subscribe(
      () => this.loadDepartments(),
      () => this.handleSaveError()
    );
  }

  private deleteDepartmentFromMarker(markerId: string, departmentId: string): void {
    this.adminService.deleteCalendarMarkerGroup(markerId, departmentId).takeUntil(this.destroy$).subscribe(
      () => this.loadDepartments(),
      () => this.handleSaveError()
    );
  }

  private toggleWeekend(day: CalendarDay): void {
    if (day.isWeekend && !day.isPublicHoliday) {
      this.deleteCalendarDay(day);
      return;
    }
    this.markWeekend(day);
  }

  private toggleHoliday(day: CalendarDay): void {
    if (day.isPublicHoliday) {
      this.deleteCalendarDay(day);
      return;
    }
    this.markHoliday(day);
  }

  private markWeekend(day: CalendarDay): void {
    this.beginCalendarDaySave(day);
    this.applyOptimisticCalendarDay(day, false);
    this.adminService.markCalendarWeekend(day.dateKey, this.activeMarkerId).takeUntil(this.destroy$).subscribe(
      () => this.loadCalendarDays(),
      () => this.handleSaveError()
    );
  }

  private markHoliday(day: CalendarDay): void {
    this.beginCalendarDaySave(day);
    this.applyOptimisticCalendarDay(day, true);
    this.adminService.markCalendarHoliday(day.dateKey, this.activeMarkerId).takeUntil(this.destroy$).subscribe(
      () => this.loadCalendarDays(),
      () => this.handleSaveError()
    );
  }

  private deleteCalendarDay(day: CalendarDay): void {
    if (!day.id) {
      return;
    }
    this.deleteExistingCalendarDay(day);
  }

  private deleteExistingCalendarDay(day: CalendarDay): void {
    var calendarDayId = day.id;
    this.beginCalendarDaySave(day);
    this.applyOptimisticCalendarDayDelete(day);
    this.adminService.delWeekends(calendarDayId, this.activeMarkerId).takeUntil(this.destroy$).subscribe(
      () => this.loadCalendarDays(),
      () => this.handleSaveError()
    );
  }

  private generateCalendarDays(): void {
    this.calendar = [];
    var selectedDate = this.getSelectedDate();
    var startDate = this.getStartDateForCalendar(selectedDate);
    this.setDisplayedMonth(selectedDate);
    this.pushCalendarDays(startDate, selectedDate.getMonth());
    this.setDateRange();
  }

  private pushCalendarDays(startDate: Date, currentMonthIndex: number): void {
    var dateToAdd = new Date(startDate);
    for (var index = 0; index < DAYS_IN_CALENDAR; index++) {
      this.calendar.push(this.buildCalendarDay(dateToAdd, currentMonthIndex, index));
      dateToAdd = this.getNextDate(dateToAdd);
    }
  }

  private buildCalendarDay(date: Date, currentMonthIndex: number, index: number): CalendarDay {
    var config = this.buildCalendarDayConfig(currentMonthIndex, index);
    return new CalendarDay(new Date(date), config);
  }

  private buildCalendarDayConfig(currentMonthIndex: number, index: number): CalendarDayConfig {
    return {
      currentMonthIndex: currentMonthIndex,
      isFirstVisibleDay: index === 0,
      calendarDays: this.calendarDays
    };
  }

  private setDateRange(): void {
    this.from = this.calendar.length ? this.calendar[0].dateKey : '';
    this.to = this.calendar.length ? this.calendar[this.calendar.length - 1].dateKey : '';
  }

  private getCalendarRequestFrom(): string {
    return addDaysToDateKey(this.from, -1);
  }

  private getCalendarRequestTo(): string {
    return addDaysToDateKey(this.to, 1);
  }

  private getSelectedDate(): Date {
    var today = new Date();
    return new Date(today.getFullYear(), today.getMonth() + this.monthIndex, 1);
  }

  private setDisplayedMonth(selectedDate: Date): void {
    this.displayMonthKey = MONTH_KEYS[selectedDate.getMonth()];
    this.displayYears = selectedDate.getFullYear();
  }

  private getStartDateForCalendar(selectedDate: Date): Date {
    var startDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 0);
    while (startDate.getDay() !== 1) {
      startDate = this.getPreviousDate(startDate);
    }
    return startDate;
  }

  private getNextDate(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  }

  private getPreviousDate(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
  }

  private buildMarkerCreateRequest(result: CalendarTimezoneMarkerCreateResult): CalendarMarkerCreateRequest {
    return {
      name: result.name,
      zoneId: result.zoneId,
      template: result.template
    };
  }

  private buildMarkerUpdateRequest(result: CalendarTimezoneMarkerEditResult): CalendarMarkerUpdateRequest {
    return {
      name: result.name,
      zoneId: result.zoneId
    };
  }

  private buildMarkerArchiveRequest(result: CalendarTimezoneMarkerArchiveResult): CalendarMarkerArchiveRequest {
    return {
      replacementMarkerId: result.replacementMarkerId
    };
  }

  private isCreateMarkerResult(
    result: CalendarTimezoneMarkerModalResult,
  ): result is CalendarTimezoneMarkerCreateResult {
    return result.mode === CalendarTimezoneMarkerModalModes.CREATE;
  }

  private isEditMarkerResult(
    result: CalendarTimezoneMarkerModalResult,
  ): result is CalendarTimezoneMarkerEditResult {
    return result.mode === CalendarTimezoneMarkerModalModes.EDIT;
  }

  private copyMarkerForDialog(marker: CalendarMarker): CalendarMarker {
    if (!marker) {
      return null;
    }
    return Object.assign({}, marker, {
      assignedDepartmentsCount: this.getDepartmentCount(marker)
    });
  }

  private buildTargetGroupIds(marker: CalendarMarker, department: CalendarMarkerGroup): string[] {
    var groupIds = this.collectMarkerGroupIds(this.getMarkerId(marker));
    addStringValue(groupIds, department.id);
    return groupIds;
  }

  private collectMarkerGroupIds(markerId: string): string[] {
    return this.departments.filter((department: CalendarMarkerGroup) => {
      return this.getDepartmentMarkerId(department) === markerId;
    }).map((department: CalendarMarkerGroup) => department.id);
  }

  private resolveCreatedMarkerId(response: HttpResponse<CalendarMarker>): string {
    if (response.body && this.getMarkerId(response.body)) {
      return this.getMarkerId(response.body);
    }
    return this.activeMarkerId;
  }

  private resolveActiveMarkerId(): string {
    if (this.hasMarkerId(this.activeMarkerId)) {
      return this.activeMarkerId;
    }
    return this.defaultMarkerId || this.firstMarkerId;
  }

  private hasMarkerId(markerId: string): boolean {
    return this.markers.some((marker: CalendarMarker) => {
      return this.getMarkerId(marker) === markerId;
    });
  }

  private get firstMarkerId(): string {
    return this.markers.length ? this.getMarkerId(this.markers[0]) : '';
  }

  private get defaultMarkerId(): string {
    var marker = this.findDefaultMarker();
    return marker ? this.getMarkerId(marker) : '';
  }

  private sortMarkers(markers: CalendarMarker[]): CalendarMarker[] {
    return this.filterVisibleMarkers(markers).sort((first: CalendarMarker, second: CalendarMarker) => {
      return this.compareMarkers(first, second);
    });
  }

  private resolveVisibleMarkers(markers: CalendarMarker[]): CalendarMarker[] {
    var visibleMarkers = this.sortMarkers(markers);
    if (visibleMarkers.length) {
      return visibleMarkers;
    }
    return [this.buildFallbackDefaultMarker()];
  }

  private toMarkerArray(markers: CalendarMarker[]): CalendarMarker[] {
    if (Array.isArray(markers)) {
      return markers;
    }
    return [];
  }

  private filterVisibleMarkers(markers: CalendarMarker[]): CalendarMarker[] {
    return markers.filter((marker: CalendarMarker) => {
      return this.isMarkerVisible(marker);
    });
  }

  private isMarkerVisible(marker: CalendarMarker): boolean {
    if (!marker) {
      return false;
    }
    if (this.isDefaultMarker(marker)) {
      return true;
    }
    return marker.active !== false;
  }

  private compareMarkers(first: CalendarMarker, second: CalendarMarker): number {
    if (this.isDefaultMarker(first)) {
      return -1;
    }
    if (this.isDefaultMarker(second)) {
      return 1;
    }
    return this.getMarkerDisplayName(first).localeCompare(this.getMarkerDisplayName(second));
  }

  private countDepartmentsByMarker(marker: CalendarMarker): number {
    return this.departments.filter((department: CalendarMarkerGroup) => {
      return this.isDepartmentAssignedToMarker(marker, department);
    }).length;
  }

  private findDefaultMarker(): CalendarMarker {
    for (var index = 0; index < this.markers.length; index++) {
      if (this.isDefaultMarker(this.markers[index])) {
        return this.markers[index];
      }
    }
    return null;
  }

  private isDefaultMarker(marker: CalendarMarker): boolean {
    return marker && marker.stock;
  }

  private buildFallbackDefaultMarker(): CalendarMarker {
    return {
      id: EMPTY_MARKER_VALUE,
      name: FALLBACK_DEFAULT_MARKER_NAME,
      active: FALLBACK_DEFAULT_MARKER_ACTIVE,
      stock: FALLBACK_DEFAULT_MARKER_STOCK,
      assignedDepartmentsCount: FALLBACK_DEFAULT_MARKER_DEPARTMENTS_COUNT,
      createdAt: EMPTY_MARKER_VALUE,
      updatedAt: EMPTY_MARKER_VALUE,
      zoneId: FALLBACK_DEFAULT_MARKER_ZONE_ID,
      effectiveZoneId: FALLBACK_DEFAULT_MARKER_ZONE_ID,
      displayName: FALLBACK_DEFAULT_MARKER_ZONE_ID,
      timezoneAssignmentRequired: FALLBACK_DEFAULT_MARKER_TIMEZONE_ASSIGNMENT_REQUIRED
    };
  }

  private isDepartmentAssignedToDefault(department: CalendarMarkerGroup): boolean {
    var markerId = this.getDepartmentMarkerId(department);
    return !markerId || markerId === this.defaultMarkerId;
  }

  private getMarkerId(marker: CalendarMarker): string {
    if (!marker) {
      return '';
    }
    return marker.id || '';
  }

  private getDepartmentMarkerId(department: CalendarMarkerGroup): string {
    return department.effectiveMarkerId || department.calendarMarkerId || '';
  }

  private sortDepartments(departments: CalendarMarkerGroup[]): CalendarMarkerGroup[] {
    return departments.slice(0).sort((first: CalendarMarkerGroup, second: CalendarMarkerGroup) => {
      return first.name.localeCompare(second.name);
    });
  }

  private handleLoadError(): void {
    this.pendingCalendarDayKey = '';
    this.isLoading = false;
    this.loadError = true;
    this.markForCheck();
  }

  private handleSaveError(): void {
    this.pendingCalendarDayKey = '';
    this.saveError = true;
    this.generateCalendarDays();
    this.markForCheck();
  }

  private handleMarkerSaveError(): void {
    this.pendingMarkerMutationId = '';
    this.saveError = true;
    this.markForCheck();
  }

  private beginCalendarDaySave(day: CalendarDay): void {
    this.pendingCalendarDayKey = day.dateKey;
    this.saveError = false;
    this.markForCheck();
  }

  private applyOptimisticCalendarDay(day: CalendarDay, isPublicHoliday: boolean): void {
    day.isWeekend = true;
    day.isPublicHoliday = isPublicHoliday;
  }

  private applyOptimisticCalendarDayDelete(day: CalendarDay): void {
    day.id = '';
    day.isWeekend = false;
    day.isPublicHoliday = false;
  }

  private markForCheck(): void {
    if (this.isDestroyed) {
      return;
    }

    try {
      this.changeDetectorRef.markForCheck();
    } catch (error) {}

    if (this.isViewRefreshQueued) {
      return;
    }

    this.isViewRefreshQueued = true;
    setTimeout(() => this.flushView(), 0);
  }

  private flushView(): void {
    this.isViewRefreshQueued = false;

    if (this.isDestroyed) {
      return;
    }

    try {
      this.changeDetectorRef.detectChanges();
    } catch (error) {
      try {
        this.changeDetectorRef.markForCheck();
      } catch (innerError) {}
    }
  }
}

function getDateKey(date: Date): string {
  var timezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timezoneOffset).toISOString().split('T')[0];
}

function addDaysToDateKey(dateKey: string, days: number): string {
  var date = parseDateKey(dateKey);
  if (!date) {
    return '';
  }
  return getDateKey(new Date(date.getFullYear(), date.getMonth(), date.getDate() + days));
}

function parseDateKey(dateKey: string): Date {
  var dateParts = dateKey ? dateKey.split('-') : [];
  if (dateParts.length !== 3) {
    return null;
  }
  return new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));
}

function isSameDate(firstDate: Date, secondDate: Date): boolean {
  return getDayTime(firstDate) === getDayTime(secondDate);
}

function getDayTime(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function findCalendarDay(calendarDays: CalendarDayResponse[], dateKey: string): CalendarDayResponse {
  for (var index = 0; index < calendarDays.length; index++) {
    if (calendarDays[index].date === dateKey) {
      return calendarDays[index];
    }
  }
  return null;
}

function addStringValue(values: string[], value: string): void {
  if (values.indexOf(value) !== -1) {
    return;
  }
  values.push(value);
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/task-fullview-popup/task-fullview-popup.component.ts